import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import unifiedApi from '../../utils/unifiedApi';

const CumulativePointsReport = ({ leagueKey }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [teamColors, setTeamColors] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Define a set of distinct colors for teams
  const colorPalette = useMemo(() => [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', 
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
    '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5',
    '#c49c94', '#f7b6d3', '#c7c7c7', '#dbdb8d', '#9edae5'
  ], []);

  useEffect(() => {
    const isWhiteColor = (color) => {
      if (!color) return false;
      const normalizedColor = color.toLowerCase().trim();
      // Check for common white color representations
      return normalizedColor === '#ffffff' || 
             normalizedColor === '#fff' || 
             normalizedColor === 'white' || 
             normalizedColor === 'rgb(255,255,255)' ||
             normalizedColor === 'rgb(255, 255, 255)';
    };

    const transformDataForChart = (reportData) => {
      if (!reportData.gameDates || reportData.gameDates.length === 0) {
        return { chartData: [], teamColors: {} };
      }

      // Get all unique teams with their color information
      const allTeams = new Map();
      reportData.gameDates.forEach(gameDate => {
        gameDate.teams.forEach(team => {
          allTeams.set(team.teamName, {
            teamId: team.teamId,
            jerseyColorHex: team.jerseyColorHex,
            jerseyColorName: team.jerseyColorName,
            logoUrl: team.logoUrl
          });
        });
      });

      const teamsArray = Array.from(allTeams.keys()).sort();
      
      // Assign colors to teams - use team jersey colors if available, otherwise fallback to palette
      const colors = {};
      teamsArray.forEach((teamName, index) => {
        const teamInfo = allTeams.get(teamName);
        if (teamInfo.jerseyColorHex) {
          // Check if the color is white and replace with grey for better visibility
          if (isWhiteColor(teamInfo.jerseyColorHex)) {
            colors[teamName] = '#666666'; // Medium grey for better visibility
          } else {
            colors[teamName] = teamInfo.jerseyColorHex;
          }
        } else {
          colors[teamName] = colorPalette[index % colorPalette.length];
        }
      });

      // Transform data for the chart - use game weeks instead of dates
      const chartData = reportData.gameDates.map((gameDate, index) => {
        const dataPoint = {
          gameWeek: `GW ${index + 1}`,
          gameWeekNumber: index + 1,
          date: new Date(gameDate.date).toLocaleDateString()
        };

        // Add cumulative points for each team
        gameDate.teams.forEach(team => {
          dataPoint[team.teamName] = team.cumulativePoints;
        });

        // Ensure all teams have a value (null if they don't appear in this game week for proper line breaks)
        teamsArray.forEach(teamName => {
          if (!(teamName in dataPoint)) {
            dataPoint[teamName] = null;
          }
        });

        return dataPoint;
      });

      return { chartData, teamColors: colors };
    };

    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!leagueKey) {
          throw new Error('League key (slug or ID) is required');
        }
        
        // Use unified endpoint that accepts both slug and ID
        const data = await unifiedApi.getLeagueCumulativePoints(leagueKey);
        
        if (!data) {
          setError('League not found or not accessible');
          return;
        }
        
        setReport(data);
        
        // Transform data for the chart
        const transformedData = transformDataForChart(data);
        setChartData(transformedData.chartData);
        setTeamColors(transformedData.teamColors);
        
      } catch (err) {
        console.error('Error fetching cumulative points report:', err);
        setError(`Failed to load report: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (leagueKey) {
      fetchReport();
    }
  }, [leagueKey, colorPalette]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const gameWeekData = chartData.find(data => data.gameWeek === label);
      return (
        <Paper sx={{ p: 2, border: '1px solid #ccc' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {label} ({gameWeekData?.date})
          </Typography>
          {payload
            .sort((a, b) => b.value - a.value)
            .map((entry, index) => (
              <Typography 
                key={index} 
                variant="body2" 
                sx={{ color: entry.color, fontSize: '0.85rem' }}
              >
                {entry.dataKey}: {entry.value} points
              </Typography>
            ))}
        </Paper>
      );
    }
    return null;
  };

  // Helper function to calculate Y-axis ticks
  const calculateYAxisTicks = useMemo(() => {
    if (!chartData.length) return { minValue: 0, maxValue: 0, ticks: [0] };
    
    const teamNames = Object.keys(teamColors);
    const allValues = chartData.flatMap(dataPoint => 
      teamNames.map(team => dataPoint[team] || 0)
    );
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    
    return { minValue, maxValue, ticks: [minValue, maxValue] };
  }, [chartData, teamColors]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading cumulative points report...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!report || !report.gameDates || report.gameDates.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No completed games found for this league.
      </Alert>
    );
  }

  const CustomLegend = ({ payload }) => {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          justifyContent: 'center', 
          gap: 2, 
          mt: 2 
        }}
      >
        {payload.map((entry, index) => (
          <Box 
            key={index}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5 
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 2,
                backgroundColor: entry.color,
                borderRadius: 1
              }}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'black',
                fontSize: '12px'
              }}
            >
              {entry.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // Chart component that can be reused in both normal and fullscreen modes
  const ChartComponent = ({ height = 500, isFullscreenMode = false }) => (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 60,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="gameWeek" 
            tick={{ fontSize: isFullscreenMode ? 14 : 12 }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis 
            tick={{ fontSize: isFullscreenMode ? 14 : 12 }}
            label={{ value: 'Cumulative Points', angle: -90, position: 'insideLeft' }}
            domain={[calculateYAxisTicks.minValue, calculateYAxisTicks.maxValue]}
            ticks={calculateYAxisTicks.ticks}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            content={<CustomLegend />}
            wrapperStyle={{ paddingTop: '20px' }}
          />
          {teamNames.map((teamName) => (
            <Line
              key={teamName}
              type="monotone"
              dataKey={teamName}
              stroke={teamColors[teamName]}
              strokeWidth={isFullscreenMode ? 4 : 3}
              dot={{ r: isFullscreenMode ? 5 : 4, strokeWidth: 2 }}
              activeDot={{ r: isFullscreenMode ? 7 : 6, strokeWidth: 2 }}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );

  const teamNames = Object.keys(teamColors);

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h3" sx={{ color: '#2E4156', fontWeight: 'bold' }}>
            Team Points Progression
          </Typography>
          <IconButton
            onClick={() => setIsFullscreen(true)}
            size="small"
            title="View Fullscreen"
          >
            <FullscreenIcon />
          </IconButton>
        </Stack>
        
        <ChartComponent height={500} />
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
        * GW = Game Week. Hover over data points to see detailed information for each game week.
      </Typography>

      {/* Fullscreen Dialog */}
      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            height: '90vh',
            maxWidth: 'none',
            maxHeight: 'none',
            m: 2
          }
        }}
      >
        <DialogActions sx={{ p: 1, justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ flex: 1, pl: 2, color: '#2E4156', fontWeight: 'bold' }}>
            Cumulative Points Chart - {report?.leagueName} (Fullscreen)
          </Typography>
          <IconButton
            onClick={() => setIsFullscreen(false)}
            size="small"
            sx={{ color: 'grey.600' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogActions>
        <DialogContent sx={{ p: 3, pt: 1 }}>
          <ChartComponent height="calc(85vh - 120px)" isFullscreenMode={true} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic', textAlign: 'center' }}>
            * GW = Game Week. Hover over data points to see detailed information for each game week.
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CumulativePointsReport;